import type { ReactNode } from 'react'
import { DragDropProvider, useDroppable } from '@dnd-kit/react'
import { Info, Plus } from 'lucide-react'
import { Link } from 'react-router-dom'
import type { CalendarActivity, CalendarResource, DayLoad } from '@/api/resourceCalendarApi'
import {
  UNASSIGNED,
  avatarTone,
  cellCount,
  cellKey,
  dayHeader,
  dayParts,
  dayTotals,
  formatHours,
  groupByCell,
  loadMeter,
  proposeDrop,
  weekSummary,
} from '@/lib/myTasks'
import { cn } from '@/lib/utils'
import { ActivityCard, type CardDragData } from './ActivityCard'
import type { MoveTarget } from './MoveDialog'
import type { PlanPreset } from './PlanTaskDialog'

interface CellData {
  resourceId: string
  date: string
}

const METER_TONE = { ok: 'bg-primary/70', busy: 'bg-amber-500', over: 'bg-destructive' } as const

/** Planned work against what is available, as a slim bar with its numbers. */
function CapacityBar({ load }: { load: DayLoad }) {
  if (load.state === 'NOT_STARTED') return <p className='text-[10px] text-muted-foreground'>Not started</p>
  if (load.state === 'OFF' && load.assignedHrs === 0) return <p className='text-[10px] text-muted-foreground'>Off</p>
  const m = loadMeter(load)
  return (
    <div title={`Shift ${formatHours(load.shiftHrs)} h · UA ${formatHours(load.uaHrs)} h · left ${formatHours(load.remainingHrs)} h`}>
      <div className='h-1 overflow-hidden rounded-full bg-muted'>
        <div className={cn('h-full rounded-full transition-[width]', METER_TONE[m.tone])} style={{ width: `${m.percent}%` }} />
      </div>
      <p
        className={cn(
          'mt-0.5 flex justify-between text-[10px] tabular-nums text-muted-foreground',
          m.tone === 'over' && 'font-semibold text-destructive',
        )}
      >
        <span>{m.label}</span>
        {load.uaHrs > 0 && <span>UA {formatHours(load.uaHrs)}</span>}
      </p>
    </div>
  )
}

function DayCell({
  resourceId,
  date,
  today,
  load,
  canManage,
  compact,
  count,
  onAdd,
  children,
}: {
  /** "3", or "2 open · 3"; empty when the cell has no cards. */
  count: string
  resourceId: string
  date: string
  today: string
  load?: DayLoad
  canManage: boolean
  compact: boolean
  onAdd: (preset: PlanPreset) => void
  children: ReactNode
}) {
  const { ref, isDropTarget } = useDroppable({
    id: cellKey(resourceId, date),
    data: { resourceId, date } satisfies CellData,
    disabled: !canManage,
  })
  const weekend = dayParts(date).weekend
  const off = !!load && load.state !== 'ON'
  return (
    <td
      ref={ref}
      className={cn(
        'group/cell relative border-b border-l border-border/50 p-1.5 align-top transition-colors',
        compact ? 'h-14' : 'h-28',
        weekend && 'bg-muted/25',
        off && 'bg-[repeating-linear-gradient(135deg,transparent,transparent_7px,theme(colors.slate.100/70)_7px,theme(colors.slate.100/70)_8px)]',
        date === today && 'bg-primary/[0.035]',
        isDropTarget && 'bg-primary/10 ring-2 ring-inset ring-primary/40',
      )}
    >
      <div className='flex h-full flex-col gap-1'>
        {count && (
          <span className='text-[10px] font-medium tabular-nums text-muted-foreground' title='Tasks in this cell'>
            {count} {count === '1' ? 'task' : 'tasks'}
          </span>
        )}
        <div className='flex flex-1 flex-col gap-1'>{children}</div>
        {load && !compact && <CapacityBar load={load} />}
        {load && compact && load.overloaded && (
          <span className='self-end rounded bg-destructive/10 px-1 text-[10px] font-semibold text-destructive'>Over</span>
        )}
      </div>
      {canManage && (
        <button
          type='button'
          title='Plan a task here'
          aria-label={`Plan a task on ${dayHeader(date)}`}
          onClick={() => onAdd({ date, ownerId: resourceId === UNASSIGNED ? null : resourceId })}
          className='absolute right-1 top-1 hidden h-5 w-5 items-center justify-center rounded-md bg-card text-muted-foreground shadow-sm ring-1 ring-border hover:text-primary focus:flex group-hover/cell:flex'
        >
          <Plus className='h-3 w-3' />
        </button>
      )}
    </td>
  )
}

function ResourcePanel({ resource, compact }: { resource: CalendarResource; compact: boolean }) {
  const shifts = [...new Set(resource.days.map((d) => d.shiftCode).filter((c): c is string => !!c))]
  const week = weekSummary(resource)
  const pct = week.availableHrs > 0 ? Math.min(100, Math.round((week.assignedHrs / week.availableHrs) * 100)) : week.assignedHrs > 0 ? 100 : 0
  return (
    <div className='flex items-center gap-2.5'>
      <span
        aria-hidden
        className={cn(
          'flex shrink-0 items-center justify-center rounded-full font-semibold',
          compact ? 'h-7 w-7 text-[10px]' : 'h-9 w-9 text-xs',
          resource.active ? avatarTone(resource.name) : 'bg-muted text-muted-foreground',
        )}
      >
        {resource.initials}
      </span>
      <div className='min-w-0 flex-1'>
        <p className='flex items-center gap-1'>
          <span className='truncate text-[13px] font-medium text-foreground'>{resource.name}</span>
          <Link
            to={`/resources/people/${encodeURIComponent(resource.userId)}`}
            title={`${resource.name}'s profile`}
            aria-label={`${resource.name}'s profile`}
            className='shrink-0 rounded text-muted-foreground hover:text-primary'
          >
            <Info className='h-3.5 w-3.5' />
          </Link>
        </p>
        <p className='truncate text-[11px] text-muted-foreground'>
          {resource.active
            ? [resource.role ?? 'No role', resource.isSupervisor ? 'Supervisor' : null, shifts.length ? `Shift ${shifts.join(', ')}` : 'No shift']
                .filter(Boolean)
                .join(' · ')
            : 'Inactive — hand their work on'}
        </p>
        {!compact && (
          <div className='mt-1 flex items-center gap-2' title='Planned this window against available hours'>
            <div className='h-1 flex-1 overflow-hidden rounded-full bg-muted'>
              <div
                className={cn('h-full rounded-full', week.overloadedDays > 0 ? 'bg-destructive' : 'bg-primary/60')}
                style={{ width: `${pct}%` }}
              />
            </div>
            <span className={cn('text-[10px] tabular-nums text-muted-foreground', week.overloadedDays > 0 && 'text-destructive')}>
              {formatHours(week.assignedHrs)}/{formatHours(week.availableHrs)} h
            </span>
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * One row per resource and one column per day, plus the Unassigned row for a
 * supervisor. It fills its container and scrolls inside it, with the day
 * headers and the people column pinned. Cards are the filtered set; each
 * cell's load is the server's and ignores the filters. Dropping a card
 * proposes a move — nothing changes until it is confirmed.
 */
export function ResourceCalendar({
  days,
  today,
  resources,
  activities,
  allActivities,
  showUnassigned,
  canManage,
  compact,
  onOpen,
  onAdd,
  onPropose,
  onMove,
}: {
  days: string[]
  today: string
  resources: CalendarResource[]
  activities: CalendarActivity[]
  /** Unfiltered, for the day totals in the header. */
  allActivities: CalendarActivity[]
  showUnassigned: boolean
  canManage: boolean
  compact: boolean
  onOpen: (activity: CalendarActivity) => void
  onAdd: (preset: PlanPreset) => void
  onPropose: (activity: CalendarActivity, target: MoveTarget) => void
  /** Change day or owner without dragging. */
  onMove: (activity: CalendarActivity) => void
}) {
  const cells = groupByCell(activities)
  const byKey = new Map(activities.map((a) => [a.key, a]))
  const rows: { id: string; resource?: CalendarResource }[] = [
    ...resources.map((r) => ({ id: r.userId, resource: r })),
    ...(showUnassigned ? [{ id: UNASSIGNED }] : []),
  ]
  const unassignedCount = activities.filter((a) => a.ownerId === null).length

  return (
    <DragDropProvider
      onDragEnd={(event) => {
        const { source, target } = event.operation
        if (event.canceled || !source || !target) return
        const activity = byKey.get((source.data as CardDragData).key)
        if (!activity) return
        const proposal = proposeDrop(activity, target.data as CellData)
        if (proposal) onPropose(activity, proposal)
      }}
    >
      <div className='overflow-x-persistent min-h-0 flex-1 overflow-y-auto rounded-2xl border bg-card shadow-sm'>
        <table className='w-full border-separate border-spacing-0 text-sm'>
          <thead>
            <tr>
              <th className='sticky left-0 top-0 z-30 w-60 min-w-[15rem] border-b bg-card/95 px-4 py-3 text-left text-xs font-medium text-muted-foreground backdrop-blur'>
                {resources.length} {resources.length === 1 ? 'person' : 'people'}
              </th>
              {days.map((d) => {
                const p = dayParts(d)
                const isToday = d === today
                const totals = dayTotals(allActivities, d)
                return (
                  <th
                    key={d}
                    className={cn(
                      'sticky top-0 z-20 min-w-[10rem] border-b border-l border-border/50 bg-card/95 px-3 py-2 text-left font-normal backdrop-blur',
                      isToday && 'bg-primary/[0.06]',
                    )}
                  >
                    <div className='flex items-center gap-2'>
                      <span
                        className={cn(
                          'flex h-8 w-8 items-center justify-center rounded-full text-base font-semibold tabular-nums',
                          isToday ? 'bg-primary text-primary-foreground' : p.weekend ? 'text-muted-foreground' : 'text-foreground',
                        )}
                      >
                        {p.day}
                      </span>
                      <span className='leading-tight'>
                        <span className={cn('block text-xs font-medium', isToday ? 'text-primary' : 'text-foreground')}>
                          {isToday ? 'Today' : p.weekday}
                        </span>
                        <span className='block text-[10px] tabular-nums text-muted-foreground'>
                          {formatHours(totals.plannedHrs)} h
                          {totals.unassigned > 0 && ` · ${totals.unassigned} open`}
                        </span>
                      </span>
                      {canManage && (
                        <button
                          type='button'
                          title='Plan a task on this day'
                          aria-label={`Plan a task on ${dayHeader(d)}`}
                          onClick={() => onAdd({ date: d })}
                          className='ml-auto flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground hover:bg-primary/10 hover:text-primary'
                        >
                          <Plus className='h-3.5 w-3.5' />
                        </button>
                      )}
                    </div>
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 && (
              <tr>
                <td colSpan={days.length + 1} className='p-10 text-center text-sm text-muted-foreground'>
                  No one matches these filters.
                </td>
              </tr>
            )}
            {rows.map((row) => (
              <tr key={row.id} className={cn(!row.resource && 'bg-muted/20')}>
                <th
                  scope='row'
                  className={cn(
                    'sticky left-0 z-10 border-b bg-card px-4 text-left align-middle font-normal',
                    compact ? 'py-1.5' : 'py-2.5',
                    !row.resource && 'bg-muted/40',
                  )}
                >
                  {row.resource ? (
                    <ResourcePanel resource={row.resource} compact={compact} />
                  ) : (
                    <div className='flex items-center gap-2.5'>
                      <span className='flex h-9 w-9 items-center justify-center rounded-full border-2 border-dashed border-muted-foreground/30 text-xs text-muted-foreground'>
                        ?
                      </span>
                      <div>
                        <p className='text-[13px] font-medium text-foreground'>Unassigned</p>
                        <p className='text-[11px] text-muted-foreground'>
                          {unassignedCount} waiting for an owner
                        </p>
                      </div>
                    </div>
                  )}
                </th>
                {days.map((d) => (
                  <DayCell
                    key={d}
                    resourceId={row.id}
                    date={d}
                    today={today}
                    load={row.resource?.days.find((x) => x.date === d)}
                    canManage={canManage}
                    compact={compact}
                    count={cellCount(cells.get(cellKey(row.id, d)) ?? [])}
                    onAdd={onAdd}
                  >
                    {(cells.get(cellKey(row.id, d)) ?? []).map((a) => (
                      <ActivityCard key={a.key} activity={a} canManage={canManage} today={today} compact={compact} onOpen={onOpen} onMove={onMove} />
                    ))}
                  </DayCell>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </DragDropProvider>
  )
}
